import api from "./index";

// Detailed interface for Aligner-specific data
export interface TreatmentPlanPrescription {
  prescriptionId: string;
  medications: string[];
}

export interface AlignerDetails {
  planName?: string;
  daysToWearEachAligner: number;
  recommendedDailyHours: number;
  upperJawOptions: number[];
  lowerJawOptions: number[];
  prescriptions: TreatmentPlanPrescription[];
}

// Detailed interface for Braces-specific data
export interface BracesDetails {
  notes: string;
  bracketType: string;
  anchorageUpperJaw: string[];
  extractLowerJawTeeth: number[];
  extractUpperJawTeeth: number[];
  anchorageLowerJaw: string[];
  durationInMonths: string;
  bracketMaterial: string;
  startDate: string; // ISO Date string
  treatmentType?: string;
  bracketSubType: string;
  prescriptions: TreatmentPlanPrescription[];
}

// Main interface for the data object sent to the API
export interface TreatmentPlanData {
  id?: undefined | string;
  patientId: string; // Assuming this links to a general treatment record
  patientName?: string; // Optional, for easier display purposes
  treatmentId?: string;
  type: "ALIGNER" | "BRACES";
  jawType?: string;
  alignerDetails?: AlignerDetails;
  bracesDetails?: BracesDetails;
}

/**
 * Creates or updates a treatment plan using multipart/form-data.
 * The 'data' parameter should conform to the TreatmentPlanData interface.
 * @param data - The JSON data for the treatment plan.
 * @param iprFiles - An array of image files for IPR details.
 */
export const createOrUpdateTreatmentPlan = async (
  data: Partial<TreatmentPlanData>, // Use Partial as either details object will be present
  iprFiles: File[]
) => {
  const formData = new FormData();

  // The API expects a 'data' field containing a JSON string
  formData.append("data", JSON.stringify(data));

  // Append each file under the 'iprDetails' field name
  iprFiles.forEach((file) => {
    formData.append("iprDetails", file);
  });

  return api.post("/treatment-plan/create-update", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// --- Other API functions remain the same ---

export interface TreatmentPlan {
  id: string;
  type?: string;
  patientId?: string;
  patientName?: string;
  clinic?: { id: string; name: string };
  iprDetails: string[];
  notes?: string;
  createdAt: string;
}

export interface TreatmentPlanPaginationFilter {
  key: string;
  value: string;
  operator: string;
}

export interface TreatmentPlanPaginationPayload {
  curPage: number;
  perPage: number;
  sortBy: string;
  direction: "asc" | "desc";
  whereClause: TreatmentPlanPaginationFilter[];
}

export interface TreatmentPlanPaginationResponse {
  statusCode: number;
  message: string;
  data: TreatmentPlan[];
  count: number;
  total: number;
  currentPage: number;
  perPage: number;
}

export const getAllTreatmentPlansWithMeta = async (
  payload?: Partial<TreatmentPlanPaginationPayload>
): Promise<TreatmentPlanPaginationResponse> => {
  const defaultPayload: TreatmentPlanPaginationPayload = {
    curPage: 1,
    perPage: 10,
    sortBy: "createdOn",
    direction: "desc",
    whereClause: [],
  };

  const finalPayload: TreatmentPlanPaginationPayload = {
    ...defaultPayload,
    ...payload,
    whereClause:
      payload?.whereClause !== undefined
        ? payload.whereClause
        : defaultPayload.whereClause,
  };

  const response = await api.post<TreatmentPlanPaginationResponse>(
    "/treatment-plan/pagination",
    finalPayload
  );

  return response.data;
};

// Backwards-compatible helper that still returns only the list
export const getAllTreatmentPlans = async (
  payload?: Partial<TreatmentPlanPaginationPayload>
): Promise<TreatmentPlan[]> => {
  const res = await getAllTreatmentPlansWithMeta(payload);
  const list = res?.data || [];
  return Array.isArray(list) ? list : [];
};

export const getTreatmentPlanById = async (id: string) => {
  const response = await api.get(`/treatment-plan/getById/${id}`);
  return response.data;
};

// Get treatment plans by patient
export const getTreatmentPlansByPatient = async (patientId: string) => {
  const res = await api.get(`/treatment-plan/by-patient/${patientId}`);
  return res;
};
export const deleteTreatmentPlan = async (id: string) => {
  return api.get(`/treatment-plan/delete/${id}`);
};

export const deleteIprImage = async (iprId: string) => {
  return api.get(`/treatment-plan/delete-ipr/${iprId}`);
};